import { useContext, useMemo } from 'react';
import { Group } from '@visx/group';
import { DataContext } from '@visx/xychart';

import { useChartContext } from '../../../components/ChartContext';
import { getScaleInput } from '../../../utils/scaleHelpers';
import { useSvgCursorPosition } from '../../utils/useSvgCursorPosition';
import { useTooltipContext } from '../Tooltip/TooltipContext';

import styles from '../../../components/ColumnChart/ColumnChart.module.css';

/**
 * BarHoverHighlight component that shows a hovered area around the active bar.
 * Only shows when the cursor is within a bar's bounds, matching tooltip behavior.
 *
 * The hovered area is a full-height rectangle with padding around the bar,
 * making it feel like the bar is inside this area.
 *
 * When tooltip is pinned (click-to-freeze feature), the highlight stays at
 * the pinned position instead of following cursor.
 */
export function BarHoverHighlight({ enabled = true }: { enabled?: boolean }) {
  const { xDataKey, chartType, zoomDomain, xScaleType } = useChartContext();
  const { isPinned, pinnedXPosition } = useTooltipContext();
  const dataContext = useContext(DataContext);

  // When pinned, don't track cursor position - use pinned position instead
  const shouldTrackCursor = enabled && !isPinned;
  const { cursorX: cursorSvgX } = useSvgCursorPosition(shouldTrackCursor);

  // Use pinned position when tooltip is pinned, otherwise use cursor position
  const activeXPosition = isPinned ? pinnedXPosition : cursorSvgX;

  // Find the hovered bar based on cursor position (or pinned position when tooltip is pinned)
  // Only returns a bar if the position is within its bounds (matching ColumnChartSnapStrategy behavior)
  const hoveredBar = useMemo(() => {
    if (!enabled || activeXPosition === null || !dataContext?.xScale || !dataContext.dataRegistry) {
      return null;
    }

    // Only process for column charts (which use band scales)
    if (chartType !== 'column') {
      return null;
    }

    const xScale = dataContext.xScale;
    const bandwidth =
      typeof (xScale as { bandwidth?: () => number }).bandwidth === 'function'
        ? (xScale as { bandwidth: () => number }).bandwidth()
        : 0;

    if (bandwidth === 0) {
      return null;
    }

    // Collect all unique x values from all series using visx's dataRegistry
    const xValues = new Set<number>();
    const dataRegistryEntries = Array.from(dataContext.dataRegistry.entries());

    for (const registryEntry of dataRegistryEntries) {
      // Skip glyphs series (they're separate registry entries for markers)
      if (registryEntry.key.endsWith('-glyphs')) {
        continue;
      }

      for (const datum of registryEntry.data) {
        const xValue = datum[xDataKey];
        if (typeof xValue === 'number') {
          xValues.add(xValue);
        }
      }
    }

    if (xValues.size === 0) {
      return null;
    }

    // Find the bar containing the active position (cursor or pinned)
    // This matches the behavior of ColumnChartSnapStrategy for band scales
    let closestXValue: number | null = null;
    let closestSvgX: number | null = null;
    let minDistance = Infinity;

    for (const xValue of xValues) {
      // Use helper to get correct input for scale (string for zoomed band scales, number otherwise)
      const scaleInput = getScaleInput(xValue, true);
      const scaledX = xScale(scaleInput as unknown as number);
      if (typeof scaledX !== 'number') {
        continue;
      }

      const bandStart = scaledX;
      const bandEnd = scaledX + bandwidth;

      // Only consider bars where active position is within bounds
      if (activeXPosition >= bandStart && activeXPosition <= bandEnd) {
        // Active position is within this bar
        // Use distance to center for prioritization if multiple bars contain position
        const bandCenter = scaledX + bandwidth / 2;
        const distance = Math.abs(bandCenter - activeXPosition);

        if (distance < minDistance) {
          minDistance = distance;
          closestXValue = xValue;
          closestSvgX = scaledX;
        }
      }
    }

    // Return the bar containing the active position (if any)
    if (closestXValue !== null && closestSvgX !== null) {
      return {
        xValue: closestXValue,
        xPosition: closestSvgX,
        bandwidth,
      };
    }

    return null;
  }, [enabled, activeXPosition, dataContext, xDataKey, chartType, zoomDomain, xScaleType]);

  // Don't render anything if no bar is hovered
  if (!hoveredBar || !dataContext?.yScale) {
    return null;
  }

  // Calculate the hover area dimensions - use 0.05% of bar width for dynamic padding
  const padding = hoveredBar.bandwidth * 0.05;
  const hoverAreaX = hoveredBar.xPosition - padding;
  const hoverAreaWidth = hoveredBar.bandwidth + padding * 2;

  // Get yScale to calculate where bars start (y=0) and max y value
  const yScale = dataContext.yScale;

  // Get the domain to find max y value
  const yDomain = typeof yScale.domain === 'function' ? yScale.domain() : [0, 0];
  const maxYValue = Math.max(...yDomain.map((v) => (typeof v === 'number' ? v : 0)));

  // Get SVG coordinates for y=0 (where bars start) and max y value
  const yZeroScaled = yScale(0);
  const yMaxScaled = yScale(maxYValue);
  const yZeroPosition = typeof yZeroScaled === 'number' ? yZeroScaled : 0;
  const yMaxPosition = typeof yMaxScaled === 'number' ? yMaxScaled : 0;

  // Calculate height from y=0 to max y (bars start at y=0, go up to max y)
  // For inverted scales (typical), yZeroPosition > yMaxPosition
  const hoverAreaHeight = Math.abs(yZeroPosition - yMaxPosition);
  const hoverAreaY = Math.min(yZeroPosition, yMaxPosition);

  return (
    <Group>
      <rect
        x={hoverAreaX}
        y={hoverAreaY}
        width={hoverAreaWidth}
        height={hoverAreaHeight}
        fill="var(--lp-color-bg-interactive-secondary-hover)"
        opacity={0.4}
        pointerEvents="none"
        className={styles.barHoverHighlight}
      />
    </Group>
  );
}
